import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  pageTitleClass,
  pageSubtitleClass,
  alertInfoClass,
  spinnerClass,
} from "../../dashboardStyles";
import DiscoverFilters from "../../../discover/DiscoverFilters";
import DiscoverResults from "../../../discover/DiscoverResults";
import { useDiscoverySearch } from "../../../discover/useDiscoverySearch";
import BookingModal from "../../../booking/BookingModal";
import { useMyProfile, toBookingPrefill } from "../../../../hooks/useMyProfile";

/**
 * The `Book` tab: search every business on the platform and book one, without
 * leaving the dashboard.
 *
 * The same search as the public `/discover` page — same hook, same filters, same
 * result cards — with the two things being signed in should buy you:
 *
 * 1. **The booking happens here.** A chosen slot opens the booking modal over
 *    the results instead of navigating to the business's marketing page. The
 *    search survives, so trying a second business is one click back, not a
 *    browser Back and a re-search.
 * 2. **The form already knows who you are.** Name, email and phone come from the
 *    profile, so the only thing left to choose is the appointment. The city of
 *    residence opens the search where the customer actually is.
 *
 * The booking itself still goes through the public endpoint, which is the one
 * that resolves availability and stages the notification outbox. A signed-in
 * customer books the same way as everyone else; their account's email is what
 * makes the result show up under "My Bookings".
 */

/**
 * The search proper, mounted only once the profile is known.
 *
 * Split out because `useDiscoverySearch` reads its opening city at mount and
 * fires the first search immediately. Mounting it before the profile arrives
 * would search the whole country, then search again — a wasted round trip and a
 * visible flash of the wrong results.
 */
const BookingSearch = ({ defaultCity, prefill }) => {
  const { t } = useTranslation();
  const search = useDiscoverySearch({ defaultCity });

  /** The business and slot being booked, or null when the modal is closed. */
  const [booking, setBooking] = useState(null);

  return (
    <>
      <DiscoverFilters
        values={search.values}
        options={search.options}
        resultCount={search.results.total}
        onChange={search.handleFiltersChange}
        onReset={search.reset}
      />

      {/* Said once, at the top, rather than as a badge on every card: the
          customer needs to know their details are already on file *before* they
          pick a time, not after. */}
      {prefill ? (
        <div className={`${alertInfoClass} mt-5 mb-0`}>
          <i className="bi bi-person-check mt-0.5 shrink-0" aria-hidden="true" />
          <span>
            {t("Dashboard.Book.bookingAs", {
              name: prefill.fullName,
              email: prefill.email,
            })}
          </span>
        </div>
      ) : null}

      <div className="mt-6">
        <DiscoverResults
          search={search}
          onSelectSlot={(business, slot) => setBooking({ business, slot })}
        />
      </div>

      {booking ? (
        <BookingModal
          isOpen
          onClose={() => setBooking(null)}
          organizationSlug={booking.business.slug}
          organizationName={
            booking.business.bookingTitle || booking.business.businessName
          }
          initialSelection={
            booking.slot
              ? {
                  serviceId: booking.slot.serviceId,
                  resourceId: booking.slot.resourceId,
                  date: booking.slot.date,
                  timeSlot: booking.slot.value,
                }
              : null
          }
          prefillCustomer={prefill}
        />
      ) : null}
    </>
  );
};

BookingSearch.propTypes = {
  defaultCity: PropTypes.string,
  prefill: PropTypes.shape({
    fullName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
  }),
};

const BookAppointment = () => {
  const { t } = useTranslation();
  const { profile, loading } = useMyProfile();

  return (
    <div>
      <div className="mb-6">
        <h1 className={pageTitleClass}>{t("Dashboard.Book.title")}</h1>
        <p className={pageSubtitleClass}>{t("Dashboard.Book.subtitle")}</p>
      </div>

      {loading ? (
        <div className="flex min-h-[40vh] items-center justify-center">
          <div
            role="status"
            aria-label={t("Dashboard.Book.loading")}
            className={spinnerClass}
          />
        </div>
      ) : (
        // A profile that failed to load is not an error worth showing here: the
        // search works without one, and the form falls back to asking for the
        // details as it always did. Keyed so a reload re-seeds the city.
        <BookingSearch
          key={profile?.city || ""}
          defaultCity={profile?.city || ""}
          prefill={toBookingPrefill(profile)}
        />
      )}
    </div>
  );
};

export default BookAppointment;
